import { useEffect, useMemo, useRef } from 'react'

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Formik, Form, Field } from 'formik'
import { Save, X } from 'lucide-react'
import * as Yup from 'yup'

import { translate, useLanguage } from '../../../i18n'
import { getSelectedBranchMeta, getUserDetails } from '../../../utils'
import {
  getGuardianModules,
  getRoleById,
  createRole as createRoleApi,
  updateRole as updateRoleApi,
} from '../apis'
import { ACTION_SLUG_TO_PERMISSION, PERMISSION_TO_ACTION_SLUG } from '../constant'

function chunkActions(actions) {
  const rows = []
  for (let i = 0; i < actions.length; i += 4) {
    rows.push(actions.slice(i, i + 4))
  }
  return rows
}

// Modules that come pre-selected when authoring a new role — every
// ActionSubModule is checked up-front; the user can still toggle them
// off. Identification is by the platform-facing `name` field returned
// from `/guardian-modules`, which is the same label rendered in the UI.
const DEFAULT_CHECKED_MODULE_NAMES = ['Dashboard', 'Help', 'Notification', 'Support Ticket']

// Modules whose permissions are mandatory — the checkboxes are forced
// on and disabled so the user can't ungrant them. Identification is by
// the same platform-facing `name` field as the default-check list.
const LOCKED_MODULE_NAMES = ['Dashboard', 'Help', 'Notification', 'Support Ticket']

// Permission dependency: checking any Coldroom action implicitly
// requires Facility (you can't author cold rooms without access to the
// facility tree). Toggling on a Coldroom action flips every Facility
// action on; turning Facility off cascades to every Coldroom action
// (the role would otherwise end up in an invalid state). Other parents
// are unaffected — a user may still want Facility access for non-
// coldroom reasons, so disabling Coldroom leaves Facility alone.
const DEPENDS_ON_PARENT = {
  Coldroom: 'Facility',
}
// Within-row action dependencies keyed by the action's slug. Granting
// any of these actions implicitly grants the slug(s) they list (and
// vice-versa). Create / Edit / Delete all require View — a role that
// can mutate records it can't see is invalid. Add new rows here to
// gate more actions; the cascade applies to both directions.
const ACTION_REQUIRES = {
  CREATE: ['VIEW'],
  EDIT: ['VIEW'],
  DELETE: ['VIEW'],
}
const REQUIRED_BY = Object.entries(ACTION_REQUIRES).reduce((acc, [action, deps]) => {
  deps.forEach((dep) => {
    if (!acc[dep]) acc[dep] = []
    acc[dep].push(action)
  })
  return acc
}, {})
// Reverse lookup — given a parent module name, returns the child
// modules that depend on it. Mirrors DEPENDS_ON_PARENT so the cascade
// direction is symmetric and easy to extend.
const CHILDREN_OF_PARENT = Object.entries(DEPENDS_ON_PARENT).reduce((acc, [child, parent]) => {
  if (!acc[parent]) acc[parent] = []
  acc[parent].push(child)
  return acc
}, {})
// Disable every action on a module in `next` so a "turn off" cascade
// can't leave a role in an invalid state (e.g. Coldroom granted while
// Facility isn't).
function disableModule(next, module, current) {
  next[module.id] = { ...current[module.id] }
  ;(module.ActionSubModules || []).forEach((a) => {
    next[module.id][a.id] = false
  })
}

function buildEmptyCheckedState(modules) {
  const state = {}
  modules.forEach((m) => {
    state[m.id] = {}
    ;(m.ActionSubModules || []).forEach((a) => {
      state[m.id][a.id] = false
    })
  })
  return state
}

function buildCheckedFromModules(modules) {
  const state = {}
  modules.forEach((m) => {
    state[m.id] = {}
    const defaultChecked = DEFAULT_CHECKED_MODULE_NAMES.includes(m.name)
    ;(m.ActionSubModules || []).forEach((a) => {
      state[m.id][a.id] = defaultChecked
    })
  })
  return state
}

function flattenKeys(modules) {
  const keys = []
  modules.forEach((m) => {
    ;(m.ActionSubModules || []).forEach((a) => {
      keys.push({ moduleId: m.id, actionId: a.id })
    })
  })
  return keys
}

function hasAnyPermission(checked) {
  if (!checked || typeof checked !== 'object') return false
  return Object.values(checked).some(
    (modulePerms) =>
      modulePerms && typeof modulePerms === 'object' && Object.values(modulePerms).some(Boolean)
  )
}

async function validateFormik(values) {
  const errors = {}
  try {
    await Yup.string()
      .trim()
      .required(translate('Role name is required'))
      .validate(values.name, { abortEarly: false })
  } catch (e) {
    errors.name = translate('Role name is required')
  }
  if (!hasAnyPermission(values.checked)) {
    errors.checked = translate('Please select at least one permission')
  }
  return errors
}

// Pull the active tenant + branch from the localStorage cache. `companyId`
// rides on the cached `/me` payload (`details.companyId`); the selected
// branch rides on `selectedBranchMeta`. Mirrors how the axios interceptor
// in `src/api/client.js` picks `meta.id` before falling back to `meta.uuid`
// so create/update payloads send the same identifier the API sees in the
// `branch-id` header.
function getCompanyContext() {
  const details = getUserDetails() || {}
  const meta = getSelectedBranchMeta() || {}
  const companyId = details?.type === 'COMPANY' ? details?.id : details?.companyId
  const branchId = meta.id ?? null
  return { companyId, branchId }
}

const upper = (s = '') => s.toUpperCase()

function buildPermissionsPayload(modules, checked) {
  const items = []
  modules.forEach((m) => {
    ;(m.ActionSubModules || []).forEach((a) => {
      if (checked[m.id]?.[a.id]) {
        const permission = ACTION_SLUG_TO_PERMISSION[upper(a.slug)]
        if (!permission) return
        items.push({ moduleId: m.id, permission })
      }
    })
  })
  return items
}

function useGuardianModulesQuery(enabled) {
  return useQuery({
    queryKey: ['guardian-modules'],
    queryFn: async () => {
      const { data } = await getGuardianModules()
      return data?.data?.data || []
    },
    enabled,
  })
}

function useRoleByIdQuery(roleId, enabled) {
  return useQuery({
    queryKey: ['guardian-role', roleId],
    queryFn: async () => {
      const { data } = await getRoleById(roleId)
      return data?.data?.data || data?.data || data
    },
    enabled: Boolean(enabled && roleId),
    retry: 1,
  })
}

function buildCheckedFromPermissions(modules, permissions) {
  // Start from the empty state — edits must reflect exactly what the
  // `/guardian-roles/:id` endpoint returns, NOT the new-role defaults.
  const state = buildEmptyCheckedState(modules)
  if (!permissions || modules.length === 0) return state
  permissions.forEach((p) => {
    const targetSlug = PERMISSION_TO_ACTION_SLUG[(p.permission || '').toLowerCase()]
    if (!targetSlug) return
    const module = modules.find((m) => m.id === p.moduleId)
    if (!module) return
    const action = (module.ActionSubModules || []).find(
      (a) => (a.slug || '').toUpperCase() === targetSlug
    )
    if (action) {
      if (!state[p.moduleId]) state[p.moduleId] = {}
      state[p.moduleId][action.id] = true
    }
  })
  return state
}

function PermissionGrid({ modules, checked, disabled, onToggle, onToggleModule }) {
  return (
    <Box className="flex flex-col gap-0">
      {modules.map((module, idx) => {
        const actions = module.ActionSubModules || []
        const rows = chunkActions(actions)
        // Modules in LOCKED_MODULE_NAMES (e.g. Help / Notification /
        // Support Ticket) ship with their permissions forced on; the
        // user must not be able to ungrant them. We disable both the
        // module-level toggle and the per-action sub-toggles for those
        // rows. `defaultChecked` (used below to keep the row visually
        // ticked when no actions are present) reuses the same list so
        // the rendered state matches the lock.
        const moduleLocked = LOCKED_MODULE_NAMES.includes(module.name)
        const moduleDisabled = disabled || moduleLocked
        const moduleChecked =
          moduleLocked || (actions.length > 0 && actions.every((a) => checked[module.id]?.[a.id]))
        const moduleSome = actions.length > 0 && actions.some((a) => checked[module.id]?.[a.id])

        return (
          <Box key={module.id}>
            <Box className="permission-row">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.2 }}>
                <Typography variant="subtitle2">{module.name}</Typography>
                <Checkbox
                  size="small"
                  checked={moduleChecked}
                  indeterminate={!moduleChecked && moduleSome}
                  onChange={(e) => onToggleModule(module.id, e.target.checked)}
                  disabled={moduleDisabled}
                  aria-label={`Toggle all ${module.name}`}
                />
              </Box>
              <Box>
                {rows.map((row, rowIdx) => (
                  <Box
                    key={rowIdx}
                    className="flex flex-wrap gap-1"
                    sx={{ mb: rowIdx < rows.length - 1 ? 0.5 : 0 }}
                  >
                    {row.map((action) => (
                      <FormControlLabel
                        key={action.id}
                        control={
                          <Checkbox
                            checked={Boolean(checked[module.id]?.[action.id])}
                            onChange={() => onToggle(module.id, action.id)}
                            disabled={moduleDisabled}
                          />
                        }
                        label={action.name}
                        sx={{ minWidth: 90 }}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
            {idx < modules.length - 1 && <Divider />}
          </Box>
        )
      })}
    </Box>
  )
}

export default function AddRoleModal({
  open,
  onClose,
  onCreated,
  onUpdated,
  role,
  viewOnly = false,
}) {
  // `viewOnly` opens the modal in read-only mode — every input and
  // checkbox is disabled, the Save button is hidden, and the title
  // reads "View Role". It still loads role details because the listing
  // payload doesn't always carry the full permissions array.
  const isView = Boolean(viewOnly)
  const isEdit = !isView && Boolean(role?.uuid)
  const roleUuid = role?.uuid ?? role?.id

  // Subscribe to language changes so the modal copy updates as soon as
  // the user switches languages. The standalone `translate()` helper
  // is still used inside `validateFormik` (defined at module scope, so
  // it can't reach this hook) — that path runs on submit and reads the
  // latest cache anyway.
  const { t } = useLanguage()

  const modulesQuery = useGuardianModulesQuery(open)
  const modules = modulesQuery.data || []

  const roleDetailQuery = useRoleByIdQuery(roleUuid, (isEdit || isView) && open)
  const effectiveRole = (isEdit || isView) && roleDetailQuery.data ? roleDetailQuery.data : role

  const initialChecked = useMemo(() => {
    if ((isEdit || isView) && Array.isArray(effectiveRole?.permissions) && modules.length > 0) {
      return buildCheckedFromPermissions(modules, effectiveRole.permissions)
    }
    return buildCheckedFromModules(modules)
  }, [modules, effectiveRole, isEdit, isView])

  const initialName = isEdit || isView ? effectiveRole?.name || '' : ''

  const mutationRef = useRef(null)

  ////
  const mutation = useMutation({
    mutationFn: async (payload) => {
      if (isEdit && roleUuid) {
        const { data } = await updateRoleApi(roleUuid, payload)
        return data
      }
      const { data } = await createRoleApi(payload)
      return data
    },
  })

  mutationRef.current = mutation

  const handleClose = () => {
    if (mutationRef.current?.isPending) return
    mutationRef.current?.reset()
    if (onClose) onClose()
  }

  useEffect(() => {
    if (!open) mutation.reset()
  }, [open])

  const apiErrorMessage = (() => {
    const err = mutation.error
    if (!err) return ''
    const msg = err?.response?.data?.message || err?.message || ''
    return Array.isArray(msg)
      ? msg.join(', ')
      : msg ||
          (isEdit
            ? t('Failed to update role. Please try again.')
            : t('Failed to save role. Please try again.'))
  })()

  const modulesErrorMessage = (() => {
    const err = modulesQuery.error
    if (!err) return ''
    const msg = err?.response?.data?.message || err?.message || ''
    return Array.isArray(msg) ? msg.join(', ') : msg || t('Failed to load modules.')
  })()

  const roleDetailErrorMessage = (() => {
    if (!(isEdit || isView) || !roleDetailQuery.isError) return ''
    const err = roleDetailQuery.error
    const msg = err?.response?.data?.message || err?.message || ''
    return Array.isArray(msg) ? msg.join(', ') : msg || t('Failed to load role details.')
  })()

  const isEditLoading = (isEdit || isView) && roleDetailQuery.isLoading
  const isEditErrored = (isEdit || isView) && roleDetailQuery.isError

  return (
    <Dialog open={open} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
      <Formik
        initialValues={{ name: initialName, checked: initialChecked }}
        enableReinitialize
        validate={validateFormik}
        onSubmit={(values, { setSubmitting }) => {
          // View mode can't submit — the Save button is hidden, but a
          // stray Enter-key submit shouldn't fire a mutation.
          if (isView) {
            setSubmitting(false)
            return
          }
          const { companyId, branchId } = getCompanyContext()

          const permissions = buildPermissionsPayload(modules, values.checked)
          const payload = isEdit
            ? {
                name: values.name.trim(),
                branchId,
                permissions,
              }
            : {
                name: values.name.trim(),
                companyId,
                branchId,
                permissions,
              }
          mutation.mutate(payload, {
            onSuccess: () => {
              setSubmitting(false)
              if (isEdit) {
                if (onUpdated) onUpdated()
              } else if (onCreated) {
                onCreated()
              }
              mutation.reset()
              if (onClose) onClose()
            },
            onError: () => setSubmitting(false),
          })
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => {
          const allKeys = flattenKeys(modules)
          const allChecked =
            allKeys.length > 0 && allKeys.every((k) => values.checked[k.moduleId]?.[k.actionId])
          const someChecked = allKeys.some((k) => values.checked[k.moduleId]?.[k.actionId])

          const handleSelectAll = (e) => {
            const val = e.target.checked
            const next = {}
            modules.forEach((m) => {
              next[m.id] = {}
              const locked = LOCKED_MODULE_NAMES.includes(m.name)
              ;(m.ActionSubModules || []).forEach((a) => {
                // Locked modules stay checked no matter what the bulk
                // toggle says — their permissions are mandatory.
                next[m.id][a.id] = locked ? true : val
              })
            })
            // Inverse cascade on bulk: if "Deselect all" empties Facility,
            // Coldroom must follow (the role would otherwise be in an
            // invalid state). Children that depend on a parent whose
            // actions are all `false` get disabled too.
            const cascadeChildren = (next) => {
              modules.forEach((parent) => {
                const children = CHILDREN_OF_PARENT[parent.name] || []
                if (!children.length) return
                const allOff = (parent.ActionSubModules || []).every(
                  (a) => next[parent.id]?.[a.id] === false
                )
                if (!allOff) return
                children.forEach((childName) => {
                  const child = modules.find((m) => m.name === childName)
                  if (!child) return
                  disableModule(next, child, next)
                })
              })
            }
            cascadeChildren(next)
            setFieldValue('checked', next)
          }

          const handleToggle = (moduleId, actionId) => {
            // Block toggles on locked modules so even programmatic
            // dispatches can't ungrant them.
            const target = modules.find((m) => m.id === moduleId)
            if (target && LOCKED_MODULE_NAMES.includes(target.name)) return
            const nextChecked = !values.checked[moduleId]?.[actionId]
            const patch = {
              [moduleId]: {
                ...values.checked[moduleId],
                [actionId]: nextChecked,
              },
            }
            // Within-row action dependency: turning Create/Edit/Delete
            // on implicitly turns View on too — a role that can mutate
            // records it can't see is invalid. Match by action slug so
            // the rule survives label renames.
            if (nextChecked && target) {
              const actionMeta = (target.ActionSubModules || []).find((a) => a.id === actionId)
              const slug = (actionMeta?.slug || '').toUpperCase()
              const requires = ACTION_REQUIRES[slug] || []
              if (requires.length) {
                requires.forEach((needSlug) => {
                  const needAction = (target.ActionSubModules || []).find(
                    (a) => (a.slug || '').toUpperCase() === needSlug
                  )
                  if (needAction) patch[moduleId][needAction.id] = true
                })
              }
            }
            // Inverse action cascade: turning View OFF also turns off
            // every action that depends on it (Create / Edit / Delete),
            // so we don't leave an invalid "mutate without view" state.
            if (!nextChecked && target) {
              const actionMeta = (target.ActionSubModules || []).find((a) => a.id === actionId)
              const slug = (actionMeta?.slug || '').toUpperCase()
              const dependents = REQUIRED_BY[slug] || []
              if (dependents.length) {
                dependents.forEach((depSlug) => {
                  const depAction = (target.ActionSubModules || []).find(
                    (a) => (a.slug || '').toUpperCase() === depSlug
                  )
                  if (depAction) patch[moduleId][depAction.id] = false
                })
              }
            }
            // Coldroom → Facility dependency: granting any Coldroom
            // permission flips every Facility action on (you can't
            // author cold rooms without facility-tree access).
            if (nextChecked && target && DEPENDS_ON_PARENT[target.name]) {
              const parentName = DEPENDS_ON_PARENT[target.name]
              const parent = modules.find((m) => m.name === parentName)
              if (parent) {
                patch[parent.id] = { ...values.checked[parent.id] }
                ;(parent.ActionSubModules || []).forEach((a) => {
                  patch[parent.id][a.id] = true
                })
              }
            }
            // Inverse cascade: turning a parent action OFF forces all
            // dependent children OFF too, so the role can't end up with
            // e.g. Coldroom granted while Facility isn't.
            if (!nextChecked && target && CHILDREN_OF_PARENT[target.name]) {
              CHILDREN_OF_PARENT[target.name].forEach((childName) => {
                const child = modules.find((m) => m.name === childName)
                if (!child) return
                disableModule(patch, child, { ...values.checked, ...patch })
              })
            }
            setFieldValue('checked', { ...values.checked, ...patch })
          }

          const handleToggleModule = (moduleId, val) => {
            const module = modules.find((m) => m.id === moduleId)
            if (!module) return
            // Locked modules ignore the toggle entirely — the parent's
            // disabled prop already prevents UI interaction, this is the
            // belt-and-braces guard for any non-UI dispatch path.
            if (LOCKED_MODULE_NAMES.includes(module.name)) return
            const next = { ...values.checked }
            next[moduleId] = {}
            ;(module.ActionSubModules || []).forEach((a) => {
              next[moduleId][a.id] = val
            })
            // Same dependency for the bulk module toggle: if the user
            // turns the entire Coldroom row on, force every Facility
            // action on too.
            if (val && DEPENDS_ON_PARENT[module.name]) {
              const parentName = DEPENDS_ON_PARENT[module.name]
              const parent = modules.find((m) => m.name === parentName)
              if (parent) {
                next[parent.id] = { ...values.checked[parent.id] }
                ;(parent.ActionSubModules || []).forEach((a) => {
                  next[parent.id][a.id] = true
                })
              }
            }
            // Inverse cascade for bulk: turning a parent OFF empties
            // every dependent child as well.
            if (!val && CHILDREN_OF_PARENT[module.name]) {
              CHILDREN_OF_PARENT[module.name].forEach((childName) => {
                const child = modules.find((m) => m.name === childName)
                if (!child) return
                disableModule(next, child, next)
              })
            }
            setFieldValue('checked', next)
          }

          return (
            <>
              <DialogTitle sx={{ pb: 0.5, pt: 2.5, px: 3 }}>
                <Box className="flex justify-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {isEdit ? t('Edit Role') : isView ? t('View Role') : t('Add Role')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                      {isEdit
                        ? t('Update the name and permissions for this role.')
                        : isView
                          ? t('View the name and permissions for this role.')
                          : t('Define a name and set granular access for each resource.')}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    aria-label={t('Close dialog')}
                    sx={{
                      color: 'text.secondary',
                      mt: 0.3,
                      // Pin both borderRadius AND a square bounding box.
                      // borderRadius: '50%' alone leaves the hover oval
                      // intact when the parent flex row stretches the
                      // button wider than it is tall — pinning width and
                      // height to the same value is what actually
                      // produces a circle.
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      padding: 0,
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                      '&:hover': { borderRadius: '50%' },
                    }}
                    disabled={mutation.isPending}
                  >
                    <X size={16} />
                  </IconButton>
                </Box>
              </DialogTitle>

              <Form>
                <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                  {apiErrorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {apiErrorMessage}
                    </Alert>
                  )}
                  {modulesErrorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {modulesErrorMessage}
                    </Alert>
                  )}
                  {roleDetailErrorMessage && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => roleDetailQuery.refetch()}
                        >
                          {t('Retry')}
                        </Button>
                      }
                    >
                      {roleDetailErrorMessage}
                    </Alert>
                  )}

                  <Typography variant="subtitle2" sx={{ mb: 0.7 }}>
                    {t('Role Name')}{' '}
                    <Box component="span" sx={{ color: 'error.main' }}>
                      *
                    </Box>
                  </Typography>
                  <Field
                    as={TextField}
                    name="name"
                    fullWidth
                    placeholder={t('e.g. Operations Manager')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    sx={{ mb: 2.5 }}
                    disabled={mutation.isPending || isEditLoading || isView}
                  />

                  <Box className="flex justify-between items-center mb-2">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {t('Administrator Access')}
                    </Typography>
                    {!isView && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allChecked}
                            indeterminate={!allChecked && someChecked}
                            onChange={handleSelectAll}
                            disabled={mutation.isPending || modulesQuery.isLoading}
                          />
                        }
                        label={t('Select All')}
                      />
                    )}
                  </Box>

                  {errors.checked && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {errors.checked}
                    </Alert>
                  )}

                  {modulesQuery.isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}

                  {isEditLoading && !modulesQuery.isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}

                  {!modulesQuery.isLoading && modules.length === 0 && !modulesErrorMessage && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 4 }}
                    >
                      {t('No modules available.')}
                    </Typography>
                  )}

                  {modules.length > 0 && !isEditLoading && !isEditErrored && (
                    <PermissionGrid
                      modules={modules}
                      checked={values.checked}
                      disabled={mutation.isPending || isView}
                      onToggle={handleToggle}
                      onToggleModule={handleToggleModule}
                    />
                  )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                  <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="inherit"
                    disabled={mutation.isPending || isSubmitting}
                  >
                    {t('Cancel')}
                  </Button>
                  {!isView && (
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save size={16} />}
                      disabled={
                        mutation.isPending ||
                        isSubmitting ||
                        modulesQuery.isLoading ||
                        modules.length === 0 ||
                        isEditLoading ||
                        isEditErrored
                      }
                    >
                      {mutation.isPending || isSubmitting
                        ? isEdit
                          ? t('Updating...')
                          : t('Saving...')
                        : isEdit
                          ? t('Update Role')
                          : t('Save Role')}
                    </Button>
                  )}
                </DialogActions>
              </Form>
            </>
          )
        }}
      </Formik>
    </Dialog>
  )
}
